'use strict';

import $ from 'jquery';
import _ from 'underscore';
import mediator from '../js/mediator';
import config from '../js/config';
import utilities from '../js/utilities';
import {makeRequest, runRequest} from '../utilities/makeRequest';
import * as globalisation from '../utilities/globalisation';
import {PromotionsService} from './PromotionsService';
import Analytics from '../components/Analytics/Analytics';
import analyticsCart from '../components/Analytics/AnalyticsCart';
import {checkCartErrorCodesMiddleware} from '../utilities/cartApiRequestPipelineMiddleware';
import cookies from 'js-cookie';
import userService from '../services/CartUserService';
import { EnableArrowReel as EnableArrowReelFeature, EnableAdobeAnalytics } from '../utilities/featureFlags';
import HybrisCartAPI from './HybrisCartAPI'
class SwaCartAPI {

  constructor() {
    this.cartState = null;
    this.cartStateError = null;
  }

  init() {
    this._getProductUrl = _.template(config.urls.cart.product);
  }

  _applyExchangeRate(value) {
    return utilities.currencyRound(utilities.applyExchangeRate(value || 0));
  }

  _cartError(json, $xhr) {
    let error = {
      level: config.error.level.error,
      title: globalisation.pluraliseAndTranslate('shoppingCart', 'apiGenericError')
    };

    switch (json.errors[0].errorCode) {
      case config.cart.apiError.alreadyInCart:
        error.level = config.error.level.info;
        error.title = globalisation.pluraliseAndTranslate('shoppingCart', 'apiAlreadyInCartTitleError');
        error.messages = [{ message: globalisation.pluraliseAndTranslate('shoppingCart', 'apiAlreadyInCartError') }];
        break;

      case config.cart.apiError.couldNotFindItem:
        error.title = globalisation.pluraliseAndTranslate('shoppingCart', 'apiCouldNotFindItemTitleError');
        error.messages = [{ message: globalisation.pluraliseAndTranslate('shoppingCart', 'apiCouldNotFindItemError') }];
        break;

      // Any other error from the API
      default:
        error.messages = [{ message: globalisation.pluraliseAndTranslate('shoppingCart', 'apiGenericError') }];
        break;
    }

    $xhr.responseJSON = {
      error: error
    };

    let defer = $.Deferred();
    defer.reject($xhr);
    return defer.promise();
  }

  getContents() {
    const getCartContentsOptions = {
      url: config.urls.cart.getCartContents,
      dataType: 'jsonp',
      data: { format: 'jsonp' }
    };

    return makeRequest(getCartContentsOptions)
      .use(checkCartErrorCodesMiddleware)
      .run()
      .then((request) => {
        HybrisCartAPI._addAdditionalCartProperties(request.response);
        return request.response;
      });
  }

  getSalesOrder() {
    let getSubTotalOptions = {
      url: config.urls.cart.getPendingSalesOrder,
      dataType: 'jsonp',
      data: { format: 'jsonp' }
    };

    return makeRequest(getSubTotalOptions)
      .use(checkCartErrorCodesMiddleware)
      .run()
      .then((request) => {
        let json = request.response;
        // Exchange rate
        json.total = this._applyExchangeRate(json.total);
        json.subtotal = this._applyExchangeRate(json.subtotal);
        return json;
      });
  }

  restoreAllCartDataFromStorage() {
    this.cartState = utilities.sessionStorageLoad('cart', this.cartState);
  }

  syncAllCartData(cartResponse = null) {
    this.cartStateError = null;
    if (cartResponse) {
      return this.syncResponseCartData(cartResponse);
    } else {
      return this.getContents()
        .then((cartContents) => {
            return this.syncResponseCartData(cartContents);
          },
          (err) => {
            Analytics.push(
              config.analytics.events.error,
              config.analytics.categories.cart,
              config.analytics.actions.errorBar,
              'CartAPI.getContents failed'
            );
            if (err.request) this.cartStateError = err.request.error;
            mediator.trigger(config.events.cart.updateFailed);
            throw err;
          });
    }
  }

  syncResponseCartData(cartContents) {
    let productsToRemove = PromotionsService.getInstance().getPromotionalProductsToRemove(cartContents);
    if (productsToRemove && productsToRemove.length) {
      return this.removeFromCart(productsToRemove)
        .then(() => this.syncAllCartData());
    }
    cartContents.total = cartContents.subtotal;
    this.cartState = cartContents;
    utilities.sessionStorageSave('cart', cartContents);
    mediator.trigger(config.events.cart.updated, cartContents);
    return cartContents;
  }

  /**
   * Add a product to the cart and sync local state
   *
   * @param {AddedProduct[]} addedProducts
   * @returns {Promise}
   */
  addProductToCart(addedProducts) {
    return this.addToCart(addedProducts, config.locale.currencyIsoCode)
      .then(cartContents => {
        cartContents.total = cartContents.subtotal;
        this.cartState = cartContents;
        utilities.sessionStorageSave('cart', cartContents);
        return cartContents;
      })
      .catch((err) => {
        err.addedProducts = addedProducts;
        throw err;
      });
  }

  /**
   * Checks if products to be added to cart has Custom reel
   *
   * @private
   * @param {AddedProduct[]} addedProducts
   * @returns {Boolean}
   */
  containsCustomReel(addedProducts) {
    return !!addedProducts.find(x => x.model.isArrowReel);
  }

  /**
   * Gets source part Id for the custom reel part
   *
   * @private
   * @param {AddedProduct[]} addedProducts
   * @returns {Boolean}
   */
  getSourcePartId(product) {
    let sourcePartId = product.model.sourcePartId;
    if (EnableArrowReelFeature.isEnabled() && product.model.isArrowReel) {
      sourcePartId = sourcePartId.replace('-AR', '');
    }
    return sourcePartId;
  }

  /**
   * Gets cart request data
   *
   * @private
   * @param {AddedProduct[]} addedProducts
   * @param {string} currency
   * @returns {object}
   */
  getCartRequestData(addedProducts, currency) {
    let requestData = {};
    requestData.format = 'jsonp';
    requestData.currency = currency;
    requestData.sourceCodes =  _.map(addedProducts, product => product.model.sourceCode).join(',');
    requestData.itemIDs = _.map(addedProducts, product => this.getSourcePartId(product)).join(',');
    requestData.quantities = _.map(addedProducts, product => product.quantity).join(',');
    if (EnableArrowReelFeature.isEnabled() && this.containsCustomReel(addedProducts)) {
      requestData.customReelFlags = _.map(addedProducts, product => product.model.isArrowReel).join(',');
    }
    return requestData;

  }

  /**
   * Add product(s) to the cart.
   *
   * @private
   * @param {AddedProduct[]} addedProducts
   * @param {string} currency
   * @returns {Promise}
   */
  addToCart(addedProducts, currency) {
    // Force USD currency
    currency = 'USD';

    const requestOptions = {
      url: config.urls.cart.addToCart,
      dataType: 'jsonp',
      addedProducts: addedProducts,
      data: this.getCartRequestData(addedProducts, currency)
    };

    return makeRequest(requestOptions)
      .use(checkCartErrorCodesMiddleware)
      .run()
      .then((request) => {
        let json = request.response;
        // Add the original product data
        json.addedProducts = addedProducts;
        HybrisCartAPI._addAdditionalCartProperties(json);
        return json;
      })
      .catch((error) => {
        error.addedProducts = addedProducts;
        throw error;
      });
  }

  /**
   * Remove items from the cart
   * @param {string} itemIDs - A comma separated list of vendorItemNo's to remove.
   * @param {array} products - An array of products to remove.
   * @returns {Promise<RequestPipeline>}
   */
  removeFromCart(products) {
    this._removeCartAnalytics(products);
    let itemIDs = products.map((product) => {
      if (product.vendorItemNo) {
        return product.vendorItemNo;
      }
    });
    itemIDs = itemIDs.join();
    const requestOptions = {
      url: config.urls.cart.removeFromCart,
      dataType: 'jsonp',
      data: { format: 'jsonp', itemIDs }
    };
    return makeRequest(requestOptions)
      .use(checkCartErrorCodesMiddleware)
      .run();
  }

  /**
   * Remove items from the cart
   * @param {string} itemIDs - A comma separated list of vendorItemNo's to remove.
   * @returns {Promise<RequestPipeline>}
   */
  removeAllItemsFromCart(products) {
    this._removeCartAnalytics(products);
    const requestOptions = {
      url: config.urls.cart.removeAllItemsFromCart,
      dataType: 'jsonp',
      data: { format: 'jsonp' }
    };
    return makeRequest(requestOptions)
      .use(checkCartErrorCodesMiddleware)
      .run();
  }

  getUpdateCartRequestData(args) {
    let requestData = {};
    requestData.format = 'jsonp';
    requestData.itemID = args.vendorItemNo;
    requestData.quantity = args.quantity;
    requestData.ipn =  args.ipn;
    if (EnableArrowReelFeature.isEnabled() && args.catalogCustomReel) {
      requestData.customReelFlag = args.catalogCustomReel;
    }
    return requestData;
  }

  updateCartEntry(args) {
    this._removeCartAnalyticsOnUpdate(args);
    const requestOptions = {
      url: config.urls.cart.updateCartEntry,
      dataType: 'jsonp',
      data: this.getUpdateCartRequestData(args)
    };

    return makeRequest(requestOptions)
      .use(checkCartErrorCodesMiddleware)
      .run();
  }

  /**
   * Get the URL needed to download a quote PDF.
   *
   * @param {string} fullName
   * @param {string} emailAddress
   * @param {Object[]} products
   * @param {string} products[].sourceCode
   * @param {string} products[].sourcePartId
   * @param {number} products[].quantity
   * @returns {string|boolean}
   */
  getQuoteDownloadLink(fullName, emailAddress, products) {
    if (!products || !products.length) {
      return false;
    }

    // Force USD currency
    let currency = 'USD';

    let data = {
      currency: currency,
      communicationType: 'web',
      leadSource: 'Arrow Web',
      sourceCode: _.map(products, product => product.sourceCode).join(','),
      itemID: _.map(products, product => product.sourcePartId).join(','),
      quantity: _.map(products, product => product.quantity).join(','),
      fullName,
      emailAddress
    };

    return config.urls.cart.downloadQuote + utilities.serialize(data);
  }

  /**
   * Make a direct backorder request.
   *
   * @param {string} firstName
   * @param {string} lastName
   * @param {string} email
   * @param {number} quantity
   * @param {string} partNumber
   * @param {string} manufacturerName
   * @param {string} type
   * @param {string} inventoryRegion
   * @param {Object} salesForceInfo
   * @param {number} [quantityAddedToCart = 0]
   * @returns {Promise.<T>}
   */
  backorder(firstName, lastName, email, quantity, partNumber, partSeo, manufacturerName, manufacturerSeo, type, inventoryRegion, salesForceInfo, quantityAddedToCart = 0) {
    let userIsLoggedIn = (this._getFedCookie()) ? true : false;
    const backorderOptions = {
      url: config.urls.cart.directBackorder,
      dataType: 'json',
      method: 'post',
      handleErrorLocally: true,
      data: this._addUtmParams({
        firstName,
        lastName,
        email,
        quantity,
        partNumber,
        partSeo,
        manufacturerName,
        manufacturerSeo,
        type,
        inventoryRegion,
        quantityAddedToCart,
        lowestPrice: salesForceInfo.lowestPrice,
        highestPrice: salesForceInfo.highestPrice,
        daysUntilDispatch: salesForceInfo.daysUntilDispatch,
        shipsFromCountryName: salesForceInfo.shipsFromCountryName,
        manufacturerLeadTime: salesForceInfo.manufacturerLeadTime,
        orderIncrement: salesForceInfo.orderIncrement,
        orderMinimumQuantity: salesForceInfo.orderMinimumQuantity
      })
    };

    if (userIsLoggedIn && !firstName && !lastName) {
      return userService.getUserInfo().then((user) => {
        backorderOptions.data = Object.assign(backorderOptions.data, user);

        if (email) {
          backorderOptions.data.email = email;
        }

        if (backorderOptions && backorderOptions.data) {
          if (backorderOptions.data.permissions) {
            backorderOptions.data.permissions = JSON.stringify(backorderOptions.data.permissions);
          }
          if (backorderOptions.data.customerNumbers) {
            backorderOptions.data.customerNumbers = JSON.stringify(backorderOptions.data.customerNumbers);
          }
        }
        return runRequest(backorderOptions);
      });
    } else {
      return runRequest(backorderOptions);
    }

  }

  /**
   * add utm params to backorder calls
   * @param params
   * @private
   */
  _addUtmParams(params) {
    const utmParams = utilities.sessionStorageLoad(config.utmStorage.key);
    if (utmParams) {
      params =  Object.assign(utmParams, params);
    }
    return params;
  }

  _getFedCookie() {
    return cookies.get(config.cookies.arrowFed);
  }

  /**
   * Adds the data needed when something in the cart has been removed.
   *
   * @param {array} products
   * @private
   *
   */
  _removeCartAnalytics(products) {
    if (EnableAdobeAnalytics.isEnabled()) {
      analyticsCart.adobeRemoveFromCart(products,'removeSingleItem');
    } else {
      let analyticsData = analyticsCart.formatProductsForRemoval(products);
      Analytics.ecommercePushWithCurrency(config.analytics.triggers.removeFromCart, analyticsData);
    }
  }

  /**
   * Decides whether datalayer should be updated with removal when cart item is updated
   * @param args
   * @private
   */
  _removeCartAnalyticsOnUpdate(args) {
    let clonedArgs = $.extend({}, args);
    // Check if updating cart is removing products from cart for dataLayer
    if (clonedArgs.quantity < clonedArgs.product.quantity) {
      // Datalayer wants the delta of desired quantity and initial quantity
      clonedArgs.product.quantity = clonedArgs.product.quantity - clonedArgs.quantity;
      this._removeCartAnalytics([args.product]);
    }
  }
}

export default SwaCartAPI;







// WEBPACK FOOTER //
// ./assets/services/SwaCartApi.js